<script setup>
import CustomMultiselect from "@/components/global-themed/CustomMultiselect.vue";
import GroupedActionPanel from "@/components/actions/partials/GroupedActionPanel.vue";
import ActionTime from "@/components/actions/partials/ActionTime.vue";
import {computed, ref, watch} from "vue";
import {useQuery, keepPreviousData} from "@tanstack/vue-query";
import axios from "axios";
import FadeTransition from "@/components/transitions/FadeTransition.vue";
import ActionCategoryIcon from "@/components/actions/partials/ActionCategoryIcon.vue";
import ZeroContent from "@/components/global-themed/ZeroContent.vue";
import ActionRow from "@/components/actions/partials/ActionRow.vue";
import {useStore} from "vuex";
import LoadingBlock from "@/components/global-themed/LoadingBlock.vue";
import PaginationButtons from "@/components/global-themed/PaginationButtons.vue";
import GroupedActionPanelTitle from "@/components/actions/partials/GroupedActionPanelTitle.vue";

const props = defineProps({
  category: {
    type: String,
    required: true,
  },
  subCategories: {
    type: Array,
    required: true,
  },
});

const store = useStore();
const activeTab = computed(() => store.getters['actions/activeTab']);

const currentPage = ref(1);
const perPage = ref(5);
const selectedSubCategory = ref('');

watch(selectedSubCategory, (newValue, oldValue) => {
  if (newValue === oldValue) {
    return;
  }

  currentPage.value = 1;
});

watch(() => props.subCategories, (newValue) => {
  if (!newValue.includes(selectedSubCategory)) {
    selectedSubCategory.value = '';
  }
});

const handlePageChange = async (page) => {
  currentPage.value = page;
}

const {
  data: actionData,
  isLoading,
} = useQuery({
  queryKey: ['actions', activeTab, props.category, currentPage, selectedSubCategory],
  queryFn: async () => {
    const query = new URLSearchParams({
      page: currentPage.value,
      perPage: perPage.value,
      actionCategory: props.category,
    });

    if (selectedSubCategory.value) {
      query.append('actionSubCategory', selectedSubCategory.value);
    }

    const {data} = await axios.get(`/ajax/v2/actions/by-category?` + query.toString());

    return data;
  },
  placeholderData: keepPreviousData,
});

// watch to see if last item was removed from page, if so fetch previous page data
watch(actionData, (newValue) => {
  if (newValue && newValue.data.length > 0) {
    return;
  }

  currentPage.value = Math.max(currentPage.value - 1, 1);
});

const totalTime = computed(() => {
  if (!actionData.value) {
    return 0;
  }

  return actionData.value.aggregates.total_action_time;
});

const paginationActionCount = computed(() => {
  if (!actionData.value) {
    return 0;
  }

  const totalActions =  actionData.value.meta.total;
  const startCount = ((currentPage.value - 1) * perPage.value) + 1;
  const endCount = Math.min(currentPage.value * perPage.value, totalActions);

  return `${startCount}-${endCount} of ${totalActions}`;
});
</script>

<template>
  <LoadingBlock v-if="isLoading" />

  <GroupedActionPanel v-else>
    <template #title>
      <div class="tw-flex tw-items-center tw-gap-6">
        <ActionCategoryIcon :category="category" />
        <GroupedActionPanelTitle :title="category" />
      </div>
    </template>

    <template #action-time>
      <FadeTransition>
        <ActionTime
          v-if="totalTime"
          :minutes="totalTime"
          class="tw-font-semibold"
        />
      </FadeTransition>
    </template>

    <template #toolbar>
      <CustomMultiselect
        :id="`selectedSubCategory${category}`"
        v-model="selectedSubCategory"
        class="tw-text-left tw-w-[325px] tw-h-12.5 tw-relative"
        :close-on-select="true"
        :options="subCategories"
        :searchable="true"
        :can-deselect="true"
        :placeholder="'Select Subcategory'"
        :can-clear="true"
      >
        <template #icon>
          <svg-vue
            icon="icons/filter-funnel"
            class="tw-w-6 tw-h-6 tw-text-grey"
          />
        </template>
      </CustomMultiselect>
    </template>

    <template #actions>
      <ActionRow
        v-for="action in actionData.data"
        :key="action.id"
        :action="action"
        :show-page-url="true"
      />
    </template>
    <template #zero-content>
      <ZeroContent
        v-if="actionData.data.length < 1"
        image="actions-completed"
      >
        {{ selectedSubCategory ? `Looks like there are no actions to display for ${selectedSubCategory} subcategory.` : 'Looks like there are no actions to display.' }}
      </ZeroContent>
    </template>

    <template #pagination>
      <div
        v-if="actionData.meta.total > perPage"
        class="tw-flex tw-items-center tw-justify-between tw-w-full"
      >
        <div class="tw-flex tw-items-center tw-justify-center tw-w-full">
          <PaginationButtons
            :last-page="actionData.meta.last_page"
            :current-page="currentPage"
            @change="(page) => handlePageChange(page)"
          />
        </div>

        <span class="tw-text-grey-dark tw-font-bold tw-text-base tw-ml-auto tw-whitespace-nowrap">
          {{ paginationActionCount }} Actions
        </span>
      </div>
    </template>
  </GroupedActionPanel>
</template>
